import type { FormApi } from 'final-form';
import { floor } from 'lodash';
import { useEffect } from 'react';
import { useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { getIsAvailableOpportunity } from '@swf/core/models/sports/utils/betSlip/getIsOpportunity';
import { getIsWalletBonusCash } from '@swf/core/models/sports/utils/extraWallets/getIsWallet';
import { dispatch, select } from '@swf/core/store';
import type { MultipleOpportunity, SingleOpportunity } from '@swf/generated/types/api/sports';
import { WalletTypeEnum } from '@swf/generated/types/api/sports';

import type { BetSlipMutators } from '../utils/betSlipFormMutators';
import { getWalletFieldName } from '../utils/getWalletFieldName';

const usePromotionPicker = (
  opportunity: SingleOpportunity | MultipleOpportunity,
  fieldName: string,
  numberOfLines: number,
  walletId?: string
) => {
  const { mutators } = useForm<FormData>() as FormApi<FormData> & { mutators: BetSlipMutators };
  const extraWallets = useSelector(select.extraWallets.getAllEnabledWallets);
  const isClientAuthenticated = useSelector(select.session.isClientAuthenticated);
  const extraWallet = extraWallets.find((wallet) => wallet.id === walletId);

  const promotionRedeemable = getIsWalletBonusCash(extraWallet);

  const promotionName = getWalletFieldName(WalletTypeEnum.BONUS_CASH, promotionRedeemable);

  const promotionFieldName = `${fieldName}.${promotionName}`;

  const isEligibleForPromotion =
    Boolean(opportunity.id) && getIsAvailableOpportunity(opportunity) && opportunity.hasBonusMoney;

  const promotionStake = extraWallet
    ? String(floor(extraWallet.amount / numberOfLines, 2))
    : undefined;

  const setPromotionFieldValue = () => {
    if (promotionFieldName && promotionStake && walletId) {
      mutators.setFieldValue(`${fieldName}.stakePerLine`, promotionStake);
      mutators.setFieldValue(promotionFieldName, [walletId]);
    }
  };

  const clearPromotionFieldValue = () => {
    mutators.clearFieldValue(`${fieldName}.stakePerLine`);
    mutators.clearFieldValue(`${fieldName}.bonusCash`);
    mutators.clearFieldValue(`${fieldName}.freeBet`);
  };

  // If the opportunity is no longer available, clear the extra wallet field
  useEffect(() => {
    if (!getIsAvailableOpportunity(opportunity)) {
      mutators.clearFieldValue(promotionFieldName);
    }
  }, [promotionFieldName, mutators, opportunity]);

  // Clear the extra wallet field if the opportunity is no longer eligible for the extra wallet
  useEffect(() => {
    if (!isEligibleForPromotion) {
      mutators.clearFieldValue(promotionFieldName);
    }
  }, [promotionFieldName, isEligibleForPromotion, mutators]);

  useEffect(() => {
    if (!isClientAuthenticated) dispatch.betSlip.promotionPickerToggled(undefined);
  }, [isClientAuthenticated]);

  return {
    setPromotionFieldValue,
    clearPromotionFieldValue
  };
};

export default usePromotionPicker;
